import { useEffect, useState } from "react";
import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/react";
import {
  IAtualizarEnderecoRequest,
  IEnderecoCobrancaRetorno,
} from "@/models/consignado/proposal-billings.model";
import { cepFormatter } from "@/utils/cepFormatter";

// Edição do endereço do tomador / empregador (seção "Dados de Contato",
// HIS-024 / TAR-049 — PUT /cobrancas/:ccb/contatos/:parte/endereco). Os
// campos e limites são os do AtualizarEnderecoDto; todos opcionais no backend.
// O empregador pode não ter endereço ainda (relação nova, sem preenchimento
// retroativo): nesse caso o formulário abre vazio e o PUT cria o registro.

type EnderecoFormValues = Required<IAtualizarEnderecoRequest>;

type Props = {
  isOpen: boolean;
  onClose: () => void;
  // "tomador" | "empregador", só pro título.
  rotuloParte: string;
  endereco: IEnderecoCobrancaRetorno | null;
  onSubmit: (values: IAtualizarEnderecoRequest) => Promise<unknown>;
  isSaving: boolean;
};

const valoresIniciais = (endereco: IEnderecoCobrancaRetorno | null): EnderecoFormValues => ({
  cep: endereco?.cep ? cepFormatter(endereco.cep) ?? "" : "",
  logradouro: endereco?.logradouro ?? "",
  numero: endereco?.numero ?? "",
  complemento: endereco?.complemento ?? "",
  bairro: endereco?.bairro ?? "",
  cidade: endereco?.cidade ?? "",
  uf: endereco?.uf ?? "",
});

export default function EnderecoFormModal({
  isOpen,
  onClose,
  rotuloParte,
  endereco,
  onSubmit,
  isSaving,
}: Props) {
  const [values, setValues] = useState<EnderecoFormValues>(() => valoresIniciais(endereco));

  useEffect(() => {
    if (isOpen) setValues(valoresIniciais(endereco));
  }, [isOpen, endereco]);

  const set = (campo: keyof EnderecoFormValues, valor: string) =>
    setValues((prev) => ({ ...prev, [campo]: valor }));

  const tudoVazio = Object.values(values).every((v) => !v.trim());

  const handleSubmit = async () => {
    if (tudoVazio) return;
    // Manda tudo (trimado): campo limpo pelo operador vai como "" e limpa no
    // banco — o backend só descarta `undefined`.
    await onSubmit({
      cep: values.cep.trim(),
      logradouro: values.logradouro.trim(),
      numero: values.numero.trim(),
      complemento: values.complemento.trim(),
      bairro: values.bairro.trim(),
      cidade: values.cidade.trim(),
      uf: values.uf.trim().toUpperCase(),
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Editar endereço do {rotuloParte}</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Flex flexDirection="column" gap={4}>
            <Grid templateColumns="repeat(3, 1fr)" gap={4}>
              <GridItem>
                <FormControl>
                  <FormLabel fontSize="sm">CEP</FormLabel>
                  <Input
                    value={values.cep}
                    onChange={(e) => set("cep", cepFormatter(e.target.value) ?? "")}
                    placeholder="00000-000"
                    fontSize="sm"
                    maxLength={9}
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={2}>
                <FormControl>
                  <FormLabel fontSize="sm">Logradouro</FormLabel>
                  <Input
                    value={values.logradouro}
                    onChange={(e) => set("logradouro", e.target.value)}
                    placeholder="Rua, avenida..."
                    fontSize="sm"
                    maxLength={150}
                  />
                </FormControl>
              </GridItem>
            </Grid>

            <Grid templateColumns="repeat(3, 1fr)" gap={4}>
              <GridItem>
                <FormControl>
                  <FormLabel fontSize="sm">Número</FormLabel>
                  <Input
                    value={values.numero}
                    onChange={(e) => set("numero", e.target.value)}
                    fontSize="sm"
                    maxLength={20}
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={2}>
                <FormControl>
                  <FormLabel fontSize="sm">Complemento</FormLabel>
                  <Input
                    value={values.complemento}
                    onChange={(e) => set("complemento", e.target.value)}
                    fontSize="sm"
                    maxLength={100}
                  />
                </FormControl>
              </GridItem>
            </Grid>

            <Grid templateColumns="2fr 2fr 1fr" gap={4}>
              <GridItem>
                <FormControl>
                  <FormLabel fontSize="sm">Bairro</FormLabel>
                  <Input
                    value={values.bairro}
                    onChange={(e) => set("bairro", e.target.value)}
                    fontSize="sm"
                    maxLength={100}
                  />
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl>
                  <FormLabel fontSize="sm">Cidade</FormLabel>
                  <Input
                    value={values.cidade}
                    onChange={(e) => set("cidade", e.target.value)}
                    fontSize="sm"
                    maxLength={100}
                  />
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl>
                  <FormLabel fontSize="sm">UF</FormLabel>
                  <Input
                    value={values.uf}
                    onChange={(e) =>
                      set("uf", e.target.value.replace(/[^a-zA-Z]/g, "").toUpperCase().slice(0, 2))
                    }
                    placeholder="UF"
                    fontSize="sm"
                    maxLength={2}
                  />
                </FormControl>
              </GridItem>
            </Grid>
          </Flex>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} size="sm" isDisabled={isSaving}>
            Cancelar
          </Button>
          <Button
            colorScheme="primary"
            onClick={handleSubmit}
            size="sm"
            isDisabled={tudoVazio}
            isLoading={isSaving}
          >
            Salvar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
